import * as Sentry from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { getSiteOrigin } from "@/lib/origin";
import {
  absoluteUrl,
  buildWebSiteJsonLd,
  SITE_NAME,
  serializeJsonLd,
} from "@/lib/seo";

const DESCRIPTION =
  "Order food online from your favourite restaurants with Munchi.";

// Search engines take the site icon and site name from the homepage, so `/` must be a real page.
export const Route = createFileRoute("/")({
  loader: () => ({ origin: getSiteOrigin() }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const url = absoluteUrl(loaderData.origin, "/");
    return {
      links: [
        { rel: "canonical", href: url },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
        { rel: "manifest", href: "/manifest.json" },
      ],
      meta: [
        { title: `${SITE_NAME} — Order Online` },
        { name: "description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:title", content: SITE_NAME },
        { property: "og:description", content: DESCRIPTION },
        {
          property: "og:image",
          content: absoluteUrl(loaderData.origin, "/logo.png"),
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: serializeJsonLd(buildWebSiteJsonLd(url)),
        },
      ],
    };
  },
  component: Sentry.withProfiler(HomePage, { name: "HomePage" }),
});

function HomePage() {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-[80vh] flex flex-col items-center justify-center gap-6 px-4 text-center"
      data-testid="home.page.root"
    >
      <img
        src="/icon.svg"
        alt={SITE_NAME}
        className="h-24 w-24 rounded-3xl shadow-md"
      />
      <h1 className="text-3xl md:text-4xl font-semibold">{t("home.title")}</h1>
      <p className="max-w-md text-muted-foreground">{t("home.description")}</p>
    </div>
  );
}
